// Confirmación estándar antes de acciones destructivas (port de modal.confirm).
// Expone confirm(opts): Promise<boolean> vía contexto.
import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { Modal, type ChipColor } from './Modal';

interface ConfirmOpts {
  title?: string;
  message?: string;
  icon?: string;
  iconColor?: ChipColor;
  confirmText?: string;
  confirmClass?: 'danger' | 'primary' | 'warning' | 'success';
}

type ConfirmFn = (opts: ConfirmOpts) => Promise<boolean>;
const Ctx = createContext<ConfirmFn>(async () => false);

interface Estado extends ConfirmOpts { open: boolean; resolve?: (v: boolean) => void; }

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [st, setSt] = useState<Estado>({ open: false });

  const confirm = useCallback<ConfirmFn>((opts) => {
    return new Promise<boolean>((resolve) => setSt({ ...opts, open: true, resolve }));
  }, []);

  const cerrar = (valor: boolean) => {
    st.resolve?.(valor);
    setSt((s) => ({ ...s, open: false }));
  };

  return (
    <Ctx.Provider value={confirm}>
      {children}
      <Modal
        open={st.open}
        title={st.title || '¿Confirmar acción?'}
        icon={st.icon || 'fa-triangle-exclamation'}
        iconColor={st.iconColor || 'danger'}
        onClose={() => cerrar(false)}
        footer={
          <>
            <button className="btn ghost" onClick={() => cerrar(false)}>Cancelar</button>
            <button className={`btn ${st.confirmClass || 'danger'}`} onClick={() => cerrar(true)}>
              {st.confirmText || 'Eliminar'}
            </button>
          </>
        }
      >
        <p style={{ margin: 0, color: 'var(--text-muted)' }}>{st.message || ''}</p>
      </Modal>
    </Ctx.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  return useContext(Ctx);
}
